import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTL, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Eye, Copy, FileText, ArrowRightLeft, Printer } from "lucide-react";

const STATUS_LABELS = {
  taslak: { label: "Taslak", cls: "bg-slate-500/15 text-slate-400" },
  gonderildi: { label: "Gönderildi", cls: "bg-blue-500/15 text-blue-500" },
  bekliyor: { label: "Bekliyor", cls: "bg-amber-500/15 text-amber-500" },
  kabul: { label: "Kabul Edildi", cls: "bg-emerald-500/15 text-emerald-500" },
  red: { label: "Reddedildi", cls: "bg-red-500/15 text-red-500" },
  sure_doldu: { label: "Süresi Doldu", cls: "bg-orange-500/15 text-orange-500" },
  satisa_donusturuldu: { label: "Satışa Dönüştürüldü", cls: "bg-primary/15 text-primary" },
};

export default function Quotes() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const nav = useNavigate();
  const load = async () => setItems((await api.get("/quotes")).data);
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Teklif silinsin mi?")) return;
    await api.delete(`/quotes/${id}`); toast.success("Silindi"); load();
  };
  const dup = async (id) => {
    const { data } = await api.post(`/quotes/${id}/duplicate`);
    toast.success("Teklif kopyalandı: " + data.number);
    load();
  };
  const convert = async (id) => {
    if (!window.confirm("Bu teklif satışa dönüştürülecek. Devam edilsin mi?")) return;
    try {
      await api.post(`/quotes/${id}/convert-to-sale`);
      toast.success("Satışa dönüştürüldü");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const filtered = items.filter((q) => statusFilter === "all" || q.status === statusFilter);
  const totalValue = filtered.reduce((s, q) => s + (q.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Teklifler</h1>
          <p className="text-sm text-muted-foreground">{filtered.length} teklif · Toplam: <span className="num font-semibold text-primary">{fmtTL(totalValue)}</span></p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => nav("/teklifler/yeni")} data-testid="new-quote-btn"><Plus size={16} className="mr-1" /> Yeni Teklif</Button>
        </div>
      </div>

      <Card className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Teklif No</TableHead>
            <TableHead>Tarih</TableHead>
            <TableHead>Müşteri</TableHead>
            <TableHead>Geçerlilik</TableHead>
            <TableHead className="text-right">Ara Toplam</TableHead>
            <TableHead className="text-right">KDV</TableHead>
            <TableHead className="text-right">Genel Toplam</TableHead>
            <TableHead>Durum</TableHead>
            <TableHead className="text-right">İşlemler</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((q) => {
              const s = STATUS_LABELS[q.status] || STATUS_LABELS.taslak;
              return (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-mono font-semibold text-primary">{q.number}</TableCell>
                  <TableCell>{fmtDate(q.date)}</TableCell>
                  <TableCell className="font-medium">{q.customer_name}</TableCell>
                  <TableCell>{q.valid_until ? fmtDate(q.valid_until) : "-"}</TableCell>
                  <TableCell className="text-right num">{fmtTL(q.subtotal)}</TableCell>
                  <TableCell className="text-right num text-muted-foreground">{fmtTL(q.vat_amount)}</TableCell>
                  <TableCell className="text-right num font-bold">{fmtTL(q.total)}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded font-semibold ${s.cls}`}>{s.label}</span></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-0.5">
                      <Button variant="ghost" size="icon" onClick={() => nav(`/teklifler/${q.id}`)} title="Görüntüle / Düzenle" data-testid={`quote-view-${q.id}`}><Eye size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => window.open(`/teklifler/${q.id}/yazdir`, "_blank")} title="PDF / Yazdır"><Printer size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => dup(q.id)} title="Kopyala"><Copy size={14} /></Button>
                      {isAdmin && q.status !== "satisa_donusturuldu" && (
                        <Button variant="ghost" size="icon" onClick={() => convert(q.id)} title="Satışa Dönüştür" className="text-emerald-500 hover:text-emerald-600" data-testid={`quote-convert-${q.id}`}><ArrowRightLeft size={14} /></Button>
                      )}
                      {isAdmin && <Button variant="ghost" size="icon" onClick={() => del(q.id)} title="Sil" data-testid={`quote-delete-${q.id}`}><Trash2 size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                <FileText size={32} className="mx-auto mb-2 opacity-40" />
                Henüz teklif oluşturulmadı
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
